/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import classNames from 'classnames';
import get from 'lodash/fp/get.js';
import getOr from 'lodash/fp/getOr.js';
import moment from 'moment';
import React from 'react';
import { useLocation } from 'react-router-dom';
import SortableTable from '#components/support/table/SortableTable/index.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import ActionCell from './ActionCell/index.js';
import ComponentIdCell from './ComponentIdCell/index.jsx';
import LocationCell from './LocationCell/index.jsx';
import QuantityCell from './QuantityCell/index.jsx';
import {
  modelComponentsTable,
  highlight,
  colComponentId,
  colName,
  colGroup,
  colLocations,
  colQuantity,
  colAction,
} from './ModelComponentsTable.module.scss';

const REFRESH_TIMEOUT = 5000;

const isWithinHighlightPeriod = recentlyAdded =>
  recentlyAdded.componentUuid && moment().diff(moment(recentlyAdded.dateTime)) <= REFRESH_TIMEOUT;

// Convert react-table v6 columns to TanStack Table v8 format
const convertColumn = (oldColumn: any, form: string) => {
  const { id, accessor, Header, Cell, sortable = true, className, headerClassName } = oldColumn;

  return {
    id,
    accessorKey: typeof accessor === 'string' ? accessor : undefined,
    accessorFn: typeof accessor === 'function' ? accessor : undefined,
    header: Header,
    enableSorting: sortable,
    // Only add cell if a custom Cell component is provided
    ...(Cell
      ? {
          cell: info => (
            <Cell
              value={info.getValue()}
              original={info.row.original}
              row={info.row.original}
              form={form}
            />
          ),
        }
      : {}),
    meta: {
      className,
      headerClassName,
    },
  };
};

const myColumns = (form: string) =>
  [
    {
      id: 'componentId',
      accessor: 'componentId',
      Header: 'ID',
      className: colComponentId,
      headerClassName: colComponentId,
      Cell: ComponentIdCell,
    },
    {
      id: 'name',
      accessor: 'name',
      Header: 'Component Name',
      className: colName,
      headerClassName: colName,
    },
    {
      id: 'group',
      accessor: 'group',
      Header: 'Type',
      className: colGroup,
      headerClassName: colGroup,
    },
    {
      id: 'locations',
      accessor: 'locations',
      Header: () => (
        <span>
          Locations <Tip tipId="rollupLocations" />
        </span>
      ),
      className: colLocations,
      headerClassName: colLocations,
      Cell: LocationCell,
    },
    {
      id: 'quantity',
      accessor: 'quantity',
      Header: () => (
        <span>
          Qty <Tip tipId="rollupTotalQuantity" />
        </span>
      ),
      className: colQuantity,
      headerClassName: colQuantity,
      Cell: QuantityCell,
    },
    {
      Header: '',
      id: 'action',
      sortable: false,
      className: classNames('p-0', colAction),
      headerClassName: colAction,
      Cell: (data: Object) => {
        const index = get('original.index', data);
        return <ActionCell index={index} form={form} />;
      },
    },
  ].map(col => convertColumn(col, form));

type PopulationItem = {
  componentUuid: string,
  index: number,
  componentId: string,
  name: string,
  group: string,
  locations: string,
};

type Props = {|
  data: Array<PopulationItem>,
  error: string,
  form: string,
|};

const ModelComponentsTable = (props: Props): Element<typeof SortableTable | 'div'> => {
  const { error, data, form } = props;

  const location = useLocation();
  const recentlyAdded = getOr({}, 'state.recentlyAdded', location);
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  const columns = React.useMemo(() => myColumns(form), [form]);

  // Reorder data to put recently added item first, but only during highlight period
  const orderedData = React.useMemo(() => {
    if (!isWithinHighlightPeriod(recentlyAdded)) {
      return data;
    }

    const recentlyAddedIndex = data.findIndex(
      item => item.componentUuid === recentlyAdded.componentUuid
    );

    if (recentlyAddedIndex === -1) {
      return data;
    }

    const recentlyAddedItem = { ...data[recentlyAddedIndex], index: 0 };
    const otherItems = data
      .filter((_, index) => index !== recentlyAddedIndex)
      .map((item, index) => ({ ...item, index: index + 1 }));

    return [recentlyAddedItem, ...otherItems];
  }, [data, recentlyAdded.componentUuid, recentlyAdded.dateTime]);

  const getRowId = React.useCallback(row => `rowForComponent-${row.componentUuid}`, []);

  const getRowClassName = React.useCallback(
    row => {
      const { componentUuid } = row;
      const isNewlyAdded =
        isWithinHighlightPeriod(recentlyAdded) && componentUuid === recentlyAdded.componentUuid;

      return isNewlyAdded ? highlight : '';
    },
    [recentlyAdded]
  );

  // Set up a timeout to force re-render when highlight period expires
  React.useEffect(() => {
    if (!recentlyAdded.componentUuid) {
      return undefined;
    }

    const timeUntilRefresh = REFRESH_TIMEOUT - moment().diff(moment(recentlyAdded.dateTime));
    if (timeUntilRefresh > 0) {
      const timer = setTimeout(forceUpdate, timeUntilRefresh);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [recentlyAdded.componentUuid, recentlyAdded.dateTime]);

  if (error) {
    return <div className="text-danger">{error}</div>;
  }

  return (
    <SortableTable
      data={orderedData}
      columns={columns}
      initialSorting={[]}
      className={classNames(['model-components-table', modelComponentsTable])}
      getRowId={getRowId}
      getRowClassName={getRowClassName}
      tableId="model-components-table"
    />
  );
};
export default ModelComponentsTable;
